import { netFormat, percentanceFormat } from "../../../helpers/formatters"
import { useI18n } from "providers/I18n"
import { useMemo } from "react"
import { formatData } from "./utils"
import { Title } from "../Tooltip/Title"
import { TooltipContentLayout } from "../Tooltip/TooltipContentLayout"
import { Box, Typography } from "@material-ui/core"
import { css } from "@emotion/css"

export const styles = {
  text: ({ color, opacity }: { color: string; opacity?: number }) => css`
    font-size: 12px;
    color: ${color};
    margin-left: 4px;
    opacity: ${opacity || 1};
    line-height: 1.2;
  `,
}

export type TooltipContentProps = {
  data: any
  firstBarOibiaPercent: string[]
  firstBarOibiaNet?: string[]
  secondBarTbgPercent?: string[]
  commonProps: any
  totalData: any
  totalsArray: number[]
  dataType: "gcf" | "non-gcf"
  showSecondBar?: boolean
  globalEntityName: string
}

export default function TooltipContent({
  data,
  firstBarOibiaPercent,
  firstBarOibiaNet,
  secondBarTbgPercent,
  commonProps,
  totalData,
  totalsArray,
  dataType,
  showSecondBar,
  globalEntityName,
}: TooltipContentProps) {
  const { t } = useI18n()
  const barData = data?.bar

  const netData = useMemo(() => {
    return firstBarOibiaNet?.map((key) =>
      formatData(key, commonProps, dataType),
    )
  }, [commonProps, dataType, firstBarOibiaNet])

  const percentanceData = useMemo(() => {
    return firstBarOibiaPercent.map((key) =>
      formatData(key, commonProps, dataType),
    )
  }, [commonProps, dataType, firstBarOibiaPercent])

  const percentageDataToDisplay =
    percentanceData?.[data.hoveredIndex || 0][barData?.key || ""]

  const netDataToDisplay = netData?.[data.hoveredIndex || 0][barData?.key || ""]

  const percentanceTbgData = useMemo(() => {
    return (
      secondBarTbgPercent?.map((key) =>
        formatData(key, commonProps, dataType),
      ) || []
    )
  }, [commonProps, dataType, secondBarTbgPercent])

  const percentageNumberOfGlobal =
    (totalData[data.hoveredIndex || 0][barData?.key || ""] * 100) /
    totalsArray[data.hoveredIndex || 0]
  const percentageOfGlobal = percentanceFormat(percentageNumberOfGlobal)

  const value = barData?.bar?.data?.[barData?.key!]

  const revenueOfGlobalValue =
    percentanceTbgData?.[data.hoveredIndex || 0]?.[barData?.key || ""] || 0

  return (
    <TooltipContentLayout>
      <Title title={barData?.key} scenario={barData?.bar?.data.title} />

      <Box display="flex" alignItems="center" style={{ gap: 4 }}>
        <Typography
          className={styles.text({ color: "var(--primary-main-light-green)" })}
        >
          {showSecondBar ? t("revenue.tbg.revenue") : t("revenue.grid.Revenue")}
          :
        </Typography>
        <Typography color="primary" className={styles.text({ color: "white" })}>
          <strong>{netFormat(value)}</strong>
        </Typography>

        <Typography
          className={
            percentanceFormat(percentageNumberOfGlobal) === "N/A" ||
            percentageNumberOfGlobal === 0
              ? styles.text({ color: "var(--other-white)" })
              : percentageNumberOfGlobal < 0
                ? styles.text({ color: "var(--other-red)" })
                : styles.text({ color: "var(--primary-main-light-green)" })
          }
          style={{ marginLeft: 1 }}
        >
          {percentageOfGlobal}{" "}
          <span
            style={{ marginLeft: 1 }}
            className={styles.text({
              color: "var(--primary-main-light-green)",
            })}
          >
            {" "}
            of {globalEntityName}{" "}
          </span>
        </Typography>
      </Box>

      <Box display="flex" alignItems="center" style={{ gap: 2 }}>
        <Typography
          className={styles.text({
            color: "var(--primary-main-light-green)",
          })}
        >
          {showSecondBar
            ? "TBG of Revenue:"
            : dataType === "non-gcf"
              ? t("revenue.oibia")
              : t("revenue.oi.percent")}
        </Typography>
        {!showSecondBar && (
          <>
            {dataType === "non-gcf" && (
              <Typography
                color="primary"
                className={styles.text({ color: "white" })}
              >
                <strong>{netFormat(netDataToDisplay)}</strong>
              </Typography>
            )}

            <Typography
              className={
                percentanceFormat(percentageDataToDisplay) === "N/A" ||
                percentageDataToDisplay === 0
                  ? styles.text({ color: "var(--other-white)" })
                  : percentageDataToDisplay < 0
                    ? styles.text({ color: "var(--other-red)" })
                    : styles.text({ color: "var(--primary-main-light-green)" })
              }
            >
              {percentanceFormat(percentageDataToDisplay)}
            </Typography>
          </>
        )}
        {showSecondBar && (
          <Typography
            className={
              percentanceFormat(revenueOfGlobalValue) === "N/A" ||
              revenueOfGlobalValue === 0
                ? styles.text({ color: "var(--other-white)" })
                : revenueOfGlobalValue < 0
                  ? styles.text({ color: "var(--other-red)" })
                  : styles.text({ color: "var(--primary-main-light-green)" })
            }
          >
            {percentanceFormat(revenueOfGlobalValue)}
          </Typography>
        )}
        <Typography
          className={styles.text({
            color: "var(--primary-main-light-green)",
          })}
          style={{ marginLeft: 1 }}
        >
          {showSecondBar ? "" : "of Revenue"}
        </Typography>
      </Box>
    </TooltipContentLayout>
  )
}
